import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { SelectedOnboardingAction } from 'global-stores';
import { NodesState } from 'views/complex-onboarding-flow/stores/states';
import { AddBlockButton } from '../add-button';
import { Label } from '../condition-label';

/* Flip to true to bring the KYB condition builder back on the canvas. See the
   note at its render site. */
const SHOW_KYB_CONDITIONS = false;
import { CustomHandle } from '../custom-handle';
import { RenderKybQuestion } from './render-kyb-question';

export interface IRenderBlock {
	label?: string | undefined;
	price?: number;
	nodeId: string;
	stepId: string;
}

export const RenderKybBlock: FC<IRenderBlock> = ({
	label,
	price,
	nodeId,
	stepId,
}) => {
	// Global variables
	const [nodes, setNodes] = useRecoilState(NodesState);
	const setSelectedOnboarding = useRecoilValue(SelectedOnboardingAction);
	const [blockCount, setBlockCount] = useState(0);

	const { conditions: kyb } = useMemo(
		() => nodes.find((el: any) => el.id === nodeId) ?? [],
		[nodes, nodeId]
	);

	/* 
	@avinashSatschel
    # Set the block count value for "modify" and "clone" operations
    # Since the node already has a block 0 and other blocks, we need to find the
    # maximum block number currently in use and set it as the block count to avoid
    # block number conflicts when modifying or cloning the node.
    # This ensures that the new node has a unique block number.
    */
	useEffect(() => {
		if (/modify|clone/.test(setSelectedOnboarding.type)) {
			const currentNodesConditions = nodes.find(
				(el: any) => el.id === nodeId
			)?.conditions;
			if (currentNodesConditions?.length > 0) {
				const allNumber = [];

				for (let i = 0; i < currentNodesConditions.length; i++) {
					allNumber.push(
						currentNodesConditions[i]?.blockId?.split('_')?.[1] ?? 0
					);
				}
				setBlockCount(Math.max(...allNumber) + 1);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const addNewBlock = useCallback(() => {
		setBlockCount(prev => prev + 1);
		const count = blockCount + 1;
		const payload = {
			and: [{}],
			blockId: `block_${count}`,
			currentId: nodeId,
		};

		setNodes((prev: any) => {
			const prevObj = JSON.parse(JSON.stringify(prev));
			const findIndex = prevObj.findIndex((el: any) => el.id === nodeId);
			prevObj[findIndex].conditions.push(payload);
			return prevObj;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blockCount, nodeId]);

	const kybBlockList = useMemo(
		() => kyb?.filter((el: any) => el.currentId === nodeId),
		[kyb, nodeId]
	);

	return (
		<>
			<CustomHandle value={nodeId} />
			<Label label={label} price={price || 0} priceLabel="Verification" lockedBox />
			{/* __simplici_kyb_no_conditions__: NO CONDITION BUILDER, matching the
				KYC & AML tile. The design of record draws these steps as a plain
				ledger of purchasable checks — a name and a price per line, then a
				total — and the per-row "Add new condition block" stacks made the
				node the tallest and busiest thing on the canvas.

				Gated rather than deleted, exactly as KYC was: the builder
				underneath (RenderKybQuestion, addNewBlock, the node's own
				`conditions` array) is untouched and one line brings it back.
				Conditions already saved on a flow still load, publish and run —
				this only removes the way to author new ones from the canvas. */}
			{SHOW_KYB_CONDITIONS && (
				<>
					<div className="questions-wrapper">
						{kybBlockList &&
							kybBlockList?.map((el: any) => (
								<Fragment key={el.nodeId}>
									<RenderKybQuestion
										nodeId={nodeId}
										blockId={el.blockId}
										stepId={stepId}
									/>
								</Fragment>
							))}
					</div>
					<AddBlockButton label="Add new condition block" onClick={addNewBlock} />
				</>
			)}
		</>
	);
};
